/**
 * MessageInput
 * Shared message input used by AI, DM, and Ritual Group chat areas.
 * Supports streaming stop (AI), edit mode (DM/Ritual), and consistent Figma styling.
 */

import { Mic, Send, Square, X } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"

interface MessageInputProps {
  input: string
  onInputChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
  isLoading: boolean
  disabled?: boolean
  placeholder?: string
  /** AI streaming: show stop button instead of send */
  onStop?: () => void
  /** DM/Ritual: show edit mode banner */
  editMode?: boolean
  onCancelEdit?: () => void
  editLabel?: string
  autoFocus?: boolean
}

export function MessageInput({
  input,
  onInputChange,
  onSubmit,
  isLoading,
  disabled,
  placeholder,
  onStop,
  editMode,
  onCancelEdit,
  editLabel,
  autoFocus,
}: MessageInputProps) {
  const { t } = useTranslation("chats")
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Append dictated speech to the existing input. We capture the current value
  // via callback ref so the recognizer doesn't see stale state across long
  // dictation sessions.
  const inputRef = useRef(input)
  useEffect(() => {
    inputRef.current = input
  }, [input])
  const handleTranscript = useCallback(
    (text: string) => {
      const current = inputRef.current
      const next = current ? `${current.trimEnd()} ${text.trim()}` : text.trim()
      onInputChange(next)
    },
    [onInputChange],
  )
  const speech = useSpeechRecognition({ onTranscript: handleTranscript })

  useEffect(() => {
    if (editMode) {
      textareaRef.current?.focus()
    }
  }, [editMode])

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus()
    }
  }, [autoFocus])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (input.trim() && !isLoading) {
        onSubmit(e)
      }
    }
    if (e.key === "Escape" && editMode) {
      onCancelEdit?.()
    }
  }

  const isStreaming = isLoading && !!onStop

  return (
    <form onSubmit={onSubmit} className="px-5 md:px-[30px] md:pb-[30px] pt-3 flex justify-center">
      <div className="w-full max-w-[925px]">
        {editMode && (
          <div className="flex items-center gap-2 px-4 pb-2 text-xs text-primary">
            <span>{editLabel}</span>
            <Button type="button" variant="ghost" size="icon-xs" onClick={onCancelEdit}>
              <X className="size-3" />
            </Button>
          </div>
        )}
        <div className="relative flex items-center h-[65px] rounded-[15px] bg-[var(--color-chat-input-bg)] border border-[var(--color-chat-input-border)]">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? t("chatInput.placeholder")}
            disabled={disabled || isLoading}
            rows={1}
            className="flex-1 resize-none bg-transparent px-[29px] py-[20px] text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50 max-h-32"
          />
          <div className="flex items-center gap-[15px] pr-[19px] shrink-0">
            {isStreaming ? (
              <button
                type="button"
                onClick={onStop}
                aria-label={t("chatInput.stopGenerating")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Square className="size-[25px]" />
              </button>
            ) : (
              <>
                {speech.isSupported && (
                  <button
                    type="button"
                    onClick={speech.toggle}
                    disabled={disabled || isLoading}
                    aria-label={
                      speech.isRecording
                        ? t("chatInput.stopDictation")
                        : t("chatInput.startDictation")
                    }
                    aria-pressed={speech.isRecording}
                    className={cn(
                      "transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
                      speech.isRecording
                        ? "text-red-500 animate-pulse"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Mic className="size-[25px]" />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!input.trim() || disabled || isLoading}
                  aria-label={t("chatInput.sendMessage")}
                  className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Send className="size-[25px]" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </form>
  )
}
